import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  ResultDialog,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { operationsApi, companiesApi } from "@/services/api";
import {
  Activity,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  HelpCircle,
  Play,
  Building2,
  ArrowUpRight,
  ArrowDownRight,
  Landmark,
  Eye,
  Filter,
  Calendar,
} from "lucide-react";
import { formatDate, formatCurrency } from "@/lib/utils";

// Tipos
interface Statement {
  id: string;
  bankEventId: string;
  bankAccountId: string;
  movementType: "CREDIT" | "DEBIT";
  category: string;
  amountCents: number;
  transactionDate: string;
  description?: string;
  counterpartName?: string;
  counterpartDocument?: string;
  counterpartBank?: string;
  analyzed: boolean;
  analyzedAt?: string;
  actionType?: string;
  status: string;
  matchedCompanyId?: string;
  matchMethod?: string;
  matchConfidence?: number;
  depositId?: string;
  withdrawalId?: string;
  applicationId?: string;
  errorMessage?: string;
  createdAt: string;
}

interface Stats {
  total: number;
  byStatus: Record<string, number>;
  byMovementType: Record<string, number>;
  byCategory: Record<string, number>;
  byAction: Record<string, number>;
  pendingMint: number;
  pendingBurn: number;
  pendingManual: number;
}

interface Company {
  id: string;
  name: string;
  document: string;
}

// Configuração de status
const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "success" | "warning" | "destructive"; icon: React.ComponentType<{ className?: string }> }> = {
  NEW: { label: "Novo", variant: "secondary", icon: Clock },
  ANALYZED: { label: "Analisado", variant: "default", icon: Eye },
  PROCESSING: { label: "Processando", variant: "warning", icon: Loader2 },
  COMPLETED: { label: "Concluído", variant: "success", icon: CheckCircle },
  FAILED: { label: "Falhou", variant: "destructive", icon: XCircle },
  IGNORED: { label: "Ignorado", variant: "secondary", icon: Eye },
  MANUAL_PENDING: { label: "Revisão Manual", variant: "warning", icon: HelpCircle },
};

// Configuração de categoria
const CATEGORY_CONFIG: Record<string, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
  PIX: { label: "PIX", icon: Activity },
  TED: { label: "TED", icon: ArrowUpRight },
  DOC: { label: "DOC", icon: ArrowUpRight },
  SISPAG: { label: "SISPAG", icon: ArrowUpRight },
  TRANSFER: { label: "Transferência", icon: ArrowUpRight },
  APPLICATION_PURCHASE: { label: "Aplicação", icon: Landmark },
  APPLICATION_REDEMPTION: { label: "Resgate", icon: Landmark },
  FEE: { label: "Tarifa", icon: AlertTriangle },
  TAX: { label: "Imposto", icon: AlertTriangle },
  INTEREST: { label: "Juros", icon: Landmark },
  OTHER: { label: "Outro", icon: Activity },
};

// Configuração de ação
const ACTION_CONFIG: Record<string, { label: string; color: string }> = {
  MINT: { label: "Emitir BRLD", color: "text-green-500" },
  BURN: { label: "Queimar BRLD", color: "text-red-500" },
  APPLICATION_RECORD: { label: "Reg. Aplicação", color: "text-blue-500" },
  REDEMPTION_RECORD: { label: "Reg. Resgate", color: "text-purple-500" },
  IGNORE: { label: "Ignorar", color: "text-gray-500" },
  MANUAL_REVIEW: { label: "Revisão", color: "text-orange-500" },
};

// Configuração de método de correspondência
const MATCH_METHOD_LABELS: Record<string, string> = {
  CNPJ_EXACT: "CNPJ Exato",
  CNPJ_PARTIAL: "CNPJ Parcial",
  NAME_EXACT: "Nome Exato",
  NAME_PARTIAL: "Nome Parcial",
};

interface AnalysisResult {
  imported: number;
  analyzed: number;
}

export function OperationsPage() {
  const [statements, setStatements] = useState<Statement[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [total, setTotal] = useState(0);

  // Estado do modal de resultado
  const [resultDialog, setResultDialog] = useState<{
    open: boolean;
    variant: "success" | "error";
    title: string;
    description?: string;
    details?: { label: string; value: string | number }[];
  }>({
    open: false,
    variant: "success",
    title: "",
  });

  // Filtros
  const [movementFilter, setMovementFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [companyFilter, setCompanyFilter] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    loadData();
  }, [movementFilter, statusFilter, categoryFilter, companyFilter, startDate, endDate]);

  async function loadCompanies() {
    try {
      const data = await companiesApi.list();
      setCompanies(data);
    } catch (error) {
      console.error("Erro ao carregar empresas:", error);
    }
  }

  async function loadData() {
    setLoading(true);
    try {
      const [statementsData, statsData] = await Promise.all([
        operationsApi.listStatements({
          movementType: movementFilter || undefined,
          status: statusFilter || undefined,
          category: categoryFilter || undefined,
          companyId: companyFilter || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          limit: 100,
        }),
        operationsApi.getStats(),
      ]);
      // Ordenar da mais recente para mais antiga
      const sorted = [...statementsData.statements].sort(
        (a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime()
      );
      setStatements(sorted);
      setTotal(statementsData.total);
      setStats(statsData);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleTriggerAnalysis() {
    setProcessing(true);
    try {
      const result: AnalysisResult = await operationsApi.triggerAnalysis();
      setResultDialog({
        open: true,
        variant: "success",
        title: "Análise concluída",
        description: "O extrato bancário foi processado com sucesso.",
        details: [
          { label: "Transações importadas", value: result.imported },
          { label: "Transações analisadas", value: result.analyzed },
        ],
      });
      await loadData();
    } catch (error) {
      console.error("Erro ao analisar:", error);
      setResultDialog({
        open: true,
        variant: "error",
        title: "Erro na análise",
        description: "Não foi possível processar o extrato bancário. Tente novamente.",
      });
    } finally {
      setProcessing(false);
    }
  }

  function clearFilters() {
    setMovementFilter("");
    setStatusFilter("");
    setCategoryFilter("");
    setCompanyFilter("");
    setStartDate("");
    setEndDate("");
  }

  function formatDocument(doc?: string) {
    if (!doc) return "-";
    // Remove mascaramento e formata
    const clean = doc.replace(/[^\d*]/g, "");
    if (clean.includes("*")) return doc; // Já mascarado
    if (clean.length === 11) {
      return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }
    if (clean.length === 14) {
      return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
    }
    return doc;
  }

  function getStatusBadge(status: string) {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.NEW;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`w-3 h-3 ${status === "PROCESSING" ? "animate-spin" : ""}`} />
        {config.label}
      </Badge>
    );
  }

  function getCategoryBadge(category: string) {
    const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.OTHER;
    const Icon = config.icon;
    return (
      <span className="flex items-center gap-1 text-sm">
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  }

  function getActionLabel(action?: string) {
    if (!action) return <span className="text-muted-foreground">-</span>;
    const config = ACTION_CONFIG[action];
    if (!config) return action;
    return <span className={config.color}>{config.label}</span>;
  }

  function getMatchMethodLabel(method?: string) {
    if (!method) return method;
    return MATCH_METHOD_LABELS[method] || method;
  }

  const hasActiveFilters = movementFilter || statusFilter || categoryFilter || companyFilter || startDate || endDate;

  if (loading && statements.length === 0) {
    return (
      <div className="p-6 space-y-6">
        <PageHeader
          icon={<Activity className="w-6 h-6" />}
          title="Operações"
          description="Análise de extrato bancário"
        />
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Activity className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Operações"
        description="Análise de extrato bancário"
        stats={
          stats
            ? [
                { value: stats.total, label: "Total" },
                { value: stats.byStatus?.COMPLETED || 0, label: "Concluídos" },
                { value: stats.pendingMint + stats.pendingBurn, label: "Pendentes" },
                { value: stats.pendingManual, label: "Revisão" },
              ]
            : []
        }
        actions={
          <div className="flex gap-2">
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={loadData}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Atualizar
            </Button>
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={handleTriggerAnalysis}
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Analisar Agora
            </Button>
          </div>
        }
      />

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card
          className={`cursor-pointer transition-all ${movementFilter === "" ? "ring-2 ring-primary" : ""}`}
          onClick={() => setMovementFilter("")}
        >
          <CardContent className="py-4">
            <p className="text-xl sm:text-2xl font-bold">{stats?.total || 0}</p>
            <p className="text-sm text-muted-foreground">Todas</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${movementFilter === "CREDIT" ? "ring-2 ring-primary" : ""}`}
          onClick={() => setMovementFilter("CREDIT")}
        >
          <CardContent className="py-4">
            <div className="flex items-center gap-2">
              <ArrowDownRight className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold text-green-500">
                  {stats?.byMovementType?.CREDIT || 0}
                </p>
                <p className="text-sm text-muted-foreground">Créditos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${movementFilter === "DEBIT" ? "ring-2 ring-primary" : ""}`}
          onClick={() => setMovementFilter("DEBIT")}
        >
          <CardContent className="py-4">
            <div className="flex items-center gap-2">
              <ArrowUpRight className="w-5 h-5 text-red-500" />
              <div>
                <p className="text-2xl font-bold text-red-500">
                  {stats?.byMovementType?.DEBIT || 0}
                </p>
                <p className="text-sm text-muted-foreground">Débitos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${statusFilter === "MANUAL_PENDING" ? "ring-2 ring-primary" : ""}`}
          onClick={() => setStatusFilter(statusFilter === "MANUAL_PENDING" ? "" : "MANUAL_PENDING")}
        >
          <CardContent className="py-4">
            <div className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-orange-500" />
              <div>
                <p className="text-2xl font-bold text-orange-500">
                  {stats?.pendingManual || 0}
                </p>
                <p className="text-sm text-muted-foreground">Revisão Manual</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros Avançados */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filtros
            </span>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Limpar filtros
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          {/* Filtros de Data e Empresa */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Data Inicial
              </label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Data Final
              </label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Building2 className="w-3 h-3" />
                Empresa
              </label>
              <select
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="">Todas as empresas</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Filtros de Categoria */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">
              Categoria
            </label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                <Badge
                  key={key}
                  variant={categoryFilter === key ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => setCategoryFilter(categoryFilter === key ? "" : key)}
                >
                  {config.label}
                  {stats?.byCategory?.[key] ? ` (${stats.byCategory[key]})` : ""}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Transações */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Extrato Bancário</span>
            <span className="text-sm font-normal text-muted-foreground">
              {statements.length} de {total}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {statements.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhuma transação encontrada</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border bg-muted/30">
                  <tr className="text-left text-sm text-muted-foreground">
                    <th className="p-4 font-medium">Data</th>
                    <th className="p-4 font-medium">Tipo</th>
                    <th className="p-4 font-medium">Contraparte</th>
                    <th className="p-4 font-medium">Valor</th>
                    <th className="p-4 font-medium">Ação</th>
                    <th className="p-4 font-medium">Situação</th>
                    <th className="p-4 font-medium">Correspondência</th>
                  </tr>
                </thead>
                <tbody>
                  {statements.map((stmt) => (
                    <tr
                      key={stmt.id}
                      className="border-b border-border last:border-0 hover:bg-muted/20"
                    >
                      <td className="p-4">
                        <p className="font-medium">{formatDate(stmt.transactionDate)}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(stmt.transactionDate).toLocaleTimeString("pt-BR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col gap-1">
                          <Badge
                            variant={stmt.movementType === "CREDIT" ? "success" : "destructive"}
                            className="w-fit"
                          >
                            {stmt.movementType === "CREDIT" ? (
                              <ArrowDownRight className="w-3 h-3 mr-1" />
                            ) : (
                              <ArrowUpRight className="w-3 h-3 mr-1" />
                            )}
                            {stmt.movementType === "CREDIT" ? "Crédito" : "Débito"}
                          </Badge>
                          {getCategoryBadge(stmt.category)}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-start gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium">
                              {stmt.counterpartName || "Não informado"}
                            </p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {formatDocument(stmt.counterpartDocument)}
                            </p>
                            {stmt.counterpartBank && (
                              <p className="text-xs text-muted-foreground">
                                Banco {stmt.counterpartBank}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <p
                          className={`font-semibold ${
                            stmt.movementType === "CREDIT" ? "text-green-500" : "text-red-500"
                          }`}
                        >
                          {stmt.movementType === "CREDIT" ? "+" : "-"}
                          {formatCurrency(stmt.amountCents / 100)}
                        </p>
                        {stmt.description && (
                          <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                            {stmt.description}
                          </p>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="text-sm font-medium">{getActionLabel(stmt.actionType)}</div>
                      </td>
                      <td className="p-4">{getStatusBadge(stmt.status)}</td>
                      <td className="p-4">
                        {stmt.matchedCompanyId ? (
                          <div className="text-xs">
                            <Badge variant="outline" className="mb-1">
                              {getMatchMethodLabel(stmt.matchMethod)}
                            </Badge>
                            <p className="text-muted-foreground">
                              Confiança: {stmt.matchConfidence}%
                            </p>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Sem correspondência</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de resultado da análise */}
      <ResultDialog
        open={resultDialog.open}
        onClose={() => setResultDialog((prev) => ({ ...prev, open: false }))}
        variant={resultDialog.variant}
        title={resultDialog.title}
        description={resultDialog.description}
        details={resultDialog.details}
      />
    </div>
  );
}
